import React, { useEffect, useState } from 'react';
import { FiAward, FiSave, FiX, FiEdit, FiTrash2, FiEye, FiUpload, FiFileText, FiUploadCloud, FiPaperclip } from 'react-icons/fi';

const EMPTY_FORM = {
    employee_id: '',
    employee_name: '',
    role: '',
    reason: '',
    employee_reply: '',
    revision_month: '',
};

const validateRevisionMonth = (value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return null;
    // Regex for DD/MM/YYYY
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
        return 'Invalid format (DD/MM/YYYY)';
    }
    const parts = trimmed.split('/');
    const d = Number(parts[0]);
    const m = Number(parts[1]);
    const y = Number(parts[2]);
    if (d < 1 || d > 31 || m < 1 || m > 12 || y < 1900 || y > 2100) {
        return 'Invalid Date';
    }
    return null;
};

const dateToDisplayFormat = (dateStr) => {
    if (!dateStr) return '';
    const trimmed = String(dateStr).trim();
    if (!trimmed) return '';

    // If YYYY-MM-DD -> DD/MM/YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        const [y, m, d] = trimmed.split('-');
        return `${d}/${m}/${y}`;
    }
    return trimmed;
};

const dateToInputFormat = (dateStr) => {
    if (!dateStr) return '';
    const trimmed = String(dateStr).trim();
    if (!trimmed) return '';

    // If YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
    }

    // If DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
        const [d, m, y] = trimmed.split('/');
        return `${y}-${m}-${d}`;
    }

    // If truncated DD/MM/Y (e.g. 14/07/2 -> 2026-07-14)
    if (/^\d{2}\/\d{2}\/\d{1,2}$/.test(trimmed)) {
        const [d, m, y] = trimmed.split('/');
        const fullYear = y.length === 1 ? `202${y}` : `20${y}`;
        return `${fullYear}-${m}-${d}`;
    }

    // If MM/YY or MM/YYYY
    if (/^\d{2}\/\d{2,4}$/.test(trimmed)) {
        const [m, y] = trimmed.split('/');
        const fullYear = y.length === 2 ? `20${y}` : y;
        return `${fullYear}-${m}-01`;
    }

    return '';
};

const formatDateToDisplay = (dateStr) => {
    if (!dateStr) return '—';
    const trimmed = String(dateStr).trim();
    if (!trimmed || trimmed === '—') return '—';

    // If YYYY-MM-DD -> DD/MM/YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        const [y, m, d] = trimmed.split('-');
        return `${d}/${m}/${y}`;
    }

    // If DD/MM/YYYY (already in standard format)
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
        return trimmed;
    }

    // If truncated DD/MM/Y (e.g. 14/07/2 or 20/07/2 -> 14/07/2026)
    if (/^\d{2}\/\d{2}\/\d{1,2}$/.test(trimmed)) {
        const [d, m, y] = trimmed.split('/');
        const fullYear = y.length === 1 ? `202${y}` : (y.length === 2 ? `20${y}` : y);
        return `${d}/${m}/${fullYear}`;
    }

    // If legacy MM/YY or MM/YYYY (e.g. 07/26 or 07/2026 -> 01/07/2026)
    if (/^\d{2}\/\d{2,4}$/.test(trimmed)) {
        const [m, y] = trimmed.split('/');
        const fullYear = y.length === 2 ? `20${y}` : y;
        return `01/${m}/${fullYear}`;
    }

    return trimmed;
};

const FieldShell = ({ label, children, error }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{label}</label>
        {children}
        {error && <p className="text-[10px] text-red-505 font-semibold">{error}</p>}
    </div>
);

const ReadOnlyValue = ({ value }) => (
    <div className="w-full px-3 py-2.5 text-sm font-semibold text-gray-800 bg-gray-55 border border-gray-200 rounded-lg">
        {value || '—'}
    </div>
);

const EmployeePerformanceRevisionModal = ({
    open,
    onClose,
    isAdmin,
    profile,
    performanceRevisions = [],
    onSave,
    onDelete,
    onUpload,
    saving = false,
    inline = false,
}) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});

    // Inline adding states
    const [isAdding, setIsAdding] = useState(false);
    const [addForm, setAddForm] = useState({
        revision_month: '',
        reason: '',
        attachment_path: '',
        attachment_names: '',
    });
    const [addErrors, setAddErrors] = useState({});

    // Inline editing row states
    const [editingRowId, setEditingRowId] = useState(null);
    const [editForm, setEditForm] = useState({
        revision_month: '',
        reason: '',
        attachment_path: '',
        attachment_names: '',
    });
    const [editErrors, setEditErrors] = useState({});
    const [uploadingId, setUploadingId] = useState(null);

    // Viewing details modal state
    const [viewingRevision, setViewingRevision] = useState(null);

    // Inline replying states
    const [editingReplyId, setEditingReplyId] = useState(null);
    const [employeeReplyText, setEmployeeReplyText] = useState('');

    useEffect(() => {
        if (!open && !inline) return;
        setForm({
            employee_id: profile?.employee_id || '',
            employee_name: profile?.employee_name || '',
            role: profile?.role || '',
            reason: profile?.reason || '',
            employee_reply: profile?.employee_reply || '',
            revision_month: profile?.revision_month || '',
        });
        setErrors({});
    }, [open, inline, profile?.employee_id, profile?.employee_name, profile?.role, profile?.reason, profile?.employee_reply, profile?.revision_month]);

    if (!open && !inline) return null;

    const setField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        if (errors[key]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }
    };

    const canEdit = (field) => {
        if (isAdmin) {
            return field !== 'employee_id' && field !== 'employee_name' && field !== 'role' && field !== 'employee_reply';
        }
        return field === 'employee_reply';
    };

    const handleSubmit = async () => {
        const nextErrors = {};
        const monthError = validateRevisionMonth(form.revision_month);
        if (monthError) nextErrors.revision_month = monthError;

        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors);
            return;
        }

        const ok = await onSave(form);
        if (ok && onClose) onClose();
    };

    const handleFileChange = async (rowId, file) => {
        if (!file) return;
        setUploadingId(rowId);
        try {
            const res = await onUpload(file);
            if (res && res.path) {
                if (rowId === 'new') {
                    setAddForm(prev => ({
                        ...prev,
                        attachment_path: res.path,
                        attachment_names: res.filename || file.name,
                    }));
                } else {
                    setEditForm(prev => ({
                        ...prev,
                        attachment_path: res.path,
                        attachment_names: res.filename || file.name,
                    }));
                }
            }
        } catch (err) {
            console.error('File upload error', err);
        } finally {
            setUploadingId(null);
        }
    };

    const handleRemoveAttachment = (rowId) => {
        if (rowId === 'new') {
            setAddForm(prev => ({
                ...prev,
                attachment_path: '',
                attachment_names: '',
            }));
        } else {
            setEditForm(prev => ({
                ...prev,
                attachment_path: '',
                attachment_names: '',
            }));
        }
    };

    const handleEditClick = (rev) => {
        setEditingRowId(rev.id);
        setEditForm({
            revision_month: rev.revision_mmyy || '',
            reason: rev.performance_revision_reason || '',
            attachment_path: rev.attachment_path || '',
            attachment_names: rev.attachment_names || '',
        });
        setEditErrors({});
    };

    const handleEditSubmit = async (id) => {
        const nextErrors = {};
        const monthError = validateRevisionMonth(editForm.revision_month);
        if (monthError) nextErrors.revision_month = monthError;

        if (Object.keys(nextErrors).length > 0) {
            setEditErrors(nextErrors);
            return;
        }

        const ok = await onSave({
            id,
            revision_month: editForm.revision_month,
            reason: editForm.reason,
            attachment_path: editForm.attachment_path,
            attachment_names: editForm.attachment_names,
        });

        if (ok) {
            setEditingRowId(null);
            setEditErrors({});
        }
    };

    const handleAddSubmit = async () => {
        const nextErrors = {};
        const monthError = validateRevisionMonth(addForm.revision_month);
        if (monthError) nextErrors.revision_month = monthError;

        if (Object.keys(nextErrors).length > 0) {
            setAddErrors(nextErrors);
            return;
        }

        const ok = await onSave({
            revision_month: addForm.revision_month,
            reason: addForm.reason,
            attachment_path: addForm.attachment_path,
            attachment_names: addForm.attachment_names,
        });

        if (ok) {
            setIsAdding(false);
            setAddForm({
                revision_month: '',
                reason: '',
                attachment_path: '',
                attachment_names: '',
            });
            setAddErrors({});
        }
    };

    const handleReplySubmit = async (revId) => {
        const ok = await onSave({
            id: revId,
            employee_reply: employeeReplyText,
            attachment_path: editForm.attachment_path,
            attachment_names: editForm.attachment_names,
        });
        if (ok) {
            setEditingReplyId(null);
            setEmployeeReplyText('');
        }
    };

    const inputClass = (field) =>
        `w-full px-3 py-2.5 text-sm font-medium border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C30010]/20 ${
            errors[field] ? 'border-red-400' : 'border-gray-200'
        } ${canEdit(field) ? 'bg-white text-gray-800' : 'bg-gray-50 text-gray-700 cursor-not-allowed'}`;

    const renderDetailsModal = () => {
        if (!viewingRevision) return null;
        return (
            <div className="fixed inset-0 z-[200] bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden border border-gray-100 animate-fade-in">
                    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-55/50">
                        <div className="flex items-center gap-2">
                            <div className="p-2 bg-[#ffebee] text-[#C30010] rounded-lg">
                                <FiEye size={16} />
                            </div>
                            <span className="text-sm font-bold text-gray-850 uppercase tracking-wide">Revision Details</span>
                        </div>
                        <button
                            type="button"
                            onClick={() => setViewingRevision(null)}
                            className="p-1 hover:bg-gray-105 rounded-full text-gray-400 transition"
                        >
                            <FiX size={16} />
                        </button>
                    </div>
                    <div className="p-6 space-y-4 text-xs">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Previous Revision Date</span>
                                <p className="text-gray-600 mt-1">{formatDateToDisplay(viewingRevision.prev_month)}</p>
                            </div>
                            <div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Current Revision Date</span>
                                <p className="text-gray-850 font-semibold mt-1">{formatDateToDisplay(viewingRevision.revision_mmyy)}</p>
                            </div>
                        </div>
                        <div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Performance Feedback</span>
                            <p className="text-gray-705 bg-gray-55 p-2.5 rounded-lg border border-gray-150 mt-1 whitespace-pre-wrap leading-relaxed">{viewingRevision.performance_revision_reason || '—'}</p>
                        </div>
                        <div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Employee Reply</span>
                            <p className="text-gray-705 bg-gray-55 p-2.5 rounded-lg border border-gray-150 mt-1 whitespace-pre-wrap leading-relaxed">{viewingRevision.performance_revision_employee_reply || '—'}</p>
                        </div>
                        <div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Uploaded Attachment</span>
                            <div className="mt-1">
                                {viewingRevision.attachment_path ? (
                                    <a
                                        href={viewingRevision.attachment_path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-[#C30010] hover:bg-red-100 rounded-lg font-bold transition"
                                    >
                                        <FiFileText size={14} />
                                        <span>{viewingRevision.attachment_names || 'View Document'}</span>
                                    </a>
                                ) : (
                                    <p className="text-gray-450 italic">No document uploaded</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="px-5 py-3.5 bg-gray-55 border-t border-gray-100 flex justify-end">
                        <button
                            type="button"
                            onClick={() => setViewingRevision(null)}
                            className="px-3.5 py-1.5 text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-55 transition"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    if (inline) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-5 py-3.5 sm:py-4 border-b border-gray-100 bg-gray-55/50 gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                        <div className="p-2 bg-[#ffebee] text-[#C30010] rounded-lg shrink-0">
                            <FiAward size={18} />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Performance Revision</h3>
                            <p className="text-[10px] text-gray-400 mt-0.5">
                                {isAdmin ? 'Create or update performance revision details' : 'Review details and submit your reply'}
                            </p>
                        </div>
                    </div>
                    {isAdmin && (
                        <button
                            type="button"
                            onClick={() => {
                                setAddForm({
                                    revision_month: '',
                                    reason: '',
                                    attachment_path: '',
                                    attachment_names: '',
                                });
                                setAddErrors({});
                                setIsAdding(true);
                            }}
                            className="w-full sm:w-auto px-3.5 py-2 sm:py-1.5 text-[11px] font-bold rounded-lg shadow-sm transition-colors inline-flex items-center justify-center gap-1.5 bg-[#C30010] text-white hover:bg-[#a0000d] shrink-0"
                        >
                            + Add Performance Revision
                        </button>
                    )}
                </div>

                <div className="p-3 sm:p-4 overflow-x-auto">
                    <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
                        <table className="w-full text-xs text-left border-collapse min-w-[750px] sm:min-w-[800px]">
                            <thead>
                                <tr className="bg-gray-100/70 text-gray-500 font-bold uppercase tracking-wider text-[9px] sm:text-[10px] border-b border-gray-200">
                                    <th className="px-3 sm:px-4 py-3 w-[15%] text-left whitespace-nowrap">Previous Revision Date</th>
                                    <th className="px-3 sm:px-4 py-3 w-[15%] text-left whitespace-nowrap">Current Revision Date</th>
                                    <th className="px-3 sm:px-4 py-3 w-[30%] text-left whitespace-nowrap">Performance Feedback</th>
                                    <th className="px-3 sm:px-4 py-3 w-[25%] text-left whitespace-nowrap">Employee Reply</th>
                                    <th className="px-3 sm:px-4 py-3 w-[8%] text-left whitespace-nowrap">Uploads</th>
                                    <th className="px-3 sm:px-4 py-3 w-[7%] text-right whitespace-nowrap">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                                                {isAdding && (
                                                                    <tr className="align-middle bg-red-50/10 border-b border-red-100 animate-fade-in">
                                                                        <td className="px-4 py-3 w-[14%] align-middle text-gray-500 font-medium">
                                                                            {performanceRevisions && performanceRevisions.length > 0 ? formatDateToDisplay(performanceRevisions[0].revision_mmyy) : '—'}
                                                                        </td>
                                                                        <td className="px-4 py-3 w-[14%] align-middle">
                                                                            <input
                                                                                type="date"
                                                                                value={dateToInputFormat(addForm.revision_month)}
                                                                                onChange={(e) => setAddForm(prev => ({ ...prev, revision_month: dateToDisplayFormat(e.target.value) }))}
                                                                                className={`w-full px-2 py-1.5 text-xs border rounded-lg focus:outline-none focus:ring-1 focus:ring-[#C30010] ${addErrors.revision_month ? 'border-red-400' : 'border-gray-200'}`}
                                                                            />
                                                                        </td>
                                                                        <td className="px-4 py-3 w-[27%] align-middle">
                                                                            <textarea
                                                                                rows={2}
                                                                                value={addForm.reason}
                                                                                onChange={(e) => setAddForm(prev => ({ ...prev, reason: e.target.value }))}
                                                                                className="w-full px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#C30010] resize-y min-h-[42px] leading-normal"
                                                                                placeholder="Performance Feedback"
                                                                            />
                                                                        </td>
                                                                        <td className="px-4 py-3 w-[26%] align-middle text-gray-400">
                                                                            —
                                                                        </td>
                                                                        <td className="px-4 py-3 w-[9%] align-middle">
                                                                            <div className="flex flex-col gap-1">
                                                                                {uploadingId === 'new' ? (
                                                                                    <span className="text-[10px] text-gray-500 animate-pulse">Uploading...</span>
                                                                                ) : addForm.attachment_path ? (
                                                                                    <div className="flex items-center gap-1 justify-between animate-fade-in">
                                                                                        <a
                                                                                            href={addForm.attachment_path}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className="text-[#C30010] hover:underline truncate max-w-[60px] font-semibold text-[10px]"
                                                                                        >
                                                                                            {addForm.attachment_names || 'File'}
                                                                                        </a>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => handleRemoveAttachment('new')}
                                                                                            className="text-red-500 hover:text-red-700"
                                                                                        >
                                                                                            <FiX size={12} />
                                                                                        </button>
                                                                                    </div>
                                                                                ) : (
                                                                                    <label className="cursor-pointer inline-flex items-center gap-1 px-2 py-1 border border-dashed border-gray-300 hover:border-[#C30010] bg-gray-50 hover:bg-red-50 rounded transition-all group text-[9px] font-medium text-gray-500 group-hover:text-[#C30010] animate-fade-in" title="Click to upload">
                                                                                        <FiUploadCloud size={12} className="text-gray-400 group-hover:text-[#C30010] shrink-0 transition-colors" />
                                                                                        <span>Upload</span>
                                                                                        <input type="file" className="hidden" onChange={(e) => handleFileChange('new', e.target.files[0])} accept="image/*,application/pdf" />
                                                                                    </label>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-4 py-3 w-[10%] align-middle text-right">
                                                                            <div className="flex flex-col items-end gap-1">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={handleAddSubmit}
                                                                                    disabled={saving}
                                                                                    className="w-14 py-1 text-[11px] font-bold text-white bg-[#C30010] hover:bg-[#a0000d] rounded shadow transition disabled:opacity-60 text-center"
                                                                                >
                                                                                    Save
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setIsAdding(false)}
                                                                                    className="w-14 py-1 text-[11px] font-bold text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-50 transition text-center"
                                                                                >
                                                                                    Cancel
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                                {(() => {
                                                                    const hasRealRevisions = performanceRevisions && performanceRevisions.length > 0;
                                                                    // Hide the empty placeholder row while adding — show only the inline add form
                                                                    const effectiveRevisions = hasRealRevisions
                                                                        ? performanceRevisions
                                                                        : (isAdding
                                                                            ? []
                                                                            : [{
                                                                                id: 'default',
                                                                                performance_revision_reason: profile?.reason || '—',
                                                                                performance_revision_employee_reply: profile?.employee_reply || '—',
                                                                                revision_mmyy: profile?.revision_month || '—'
                                                                            }]);

                                                                    return effectiveRevisions.map((rev, idx) => {
                                                                        const prevRevisionMonth = (idx + 1 < effectiveRevisions.length)
                                                                            ? (effectiveRevisions[idx + 1].revision_mmyy || '—')
                                                                            : '—';

                                                                        const isEditingReply = editingReplyId === rev.id;
                                                                        const isEditingRow = editingRowId === rev.id;

                                                                        if (isEditingRow) {
                                                                            return (
                                                                                <tr key={rev.id} className="align-middle bg-blue-50/10 border-b border-blue-100">
                                                                                    <td className="px-4 py-3 w-[14%] align-middle text-gray-500 font-medium">
                                                                                        {formatDateToDisplay(prevRevisionMonth)}
                                                                                    </td>
                                                                                    <td className="px-4 py-3 w-[14%] align-middle">
                                                                                        <input
                                                                                            type="date"
                                                                                            value={dateToInputFormat(editForm.revision_month)}
                                                                                            onChange={(e) => setEditForm(prev => ({ ...prev, revision_month: dateToDisplayFormat(e.target.value) }))}
                                                                                            className={`w-full px-2 py-1.5 text-xs border rounded-lg focus:outline-none focus:ring-1 focus:ring-[#C30010] ${editErrors.revision_month ? 'border-red-400' : 'border-gray-200'}`}
                                                                                        />
                                                                                    </td>
                                                                                    <td className="px-4 py-3 w-[27%] align-middle">
                                                                                        <textarea
                                                                                            rows={2}
                                                                                            value={editForm.reason}
                                                                                            onChange={(e) => setEditForm(prev => ({ ...prev, reason: e.target.value }))}
                                                                                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#C30010] resize-y min-h-[42px] leading-normal"
                                                                                            placeholder="Performance Feedback"
                                                                                        />
                                                                                    </td>
                                                                                    <td className="px-4 py-3 w-[26%] align-middle text-gray-500">
                                                                                        {rev.performance_revision_employee_reply || '—'}
                                                                                    </td>
                                                                                    <td className="px-4 py-3 w-[9%] align-middle">
                                                                                        <div className="flex flex-col gap-1">
                                                                                            {uploadingId === rev.id ? (
                                                                                                <span className="text-[10px] text-gray-500 animate-pulse">Uploading...</span>
                                                                                            ) : editForm.attachment_path ? (
                                                                                                <div className="flex items-center gap-1 justify-between animate-fade-in">
                                                                                                    <a
                                                                                                        href={editForm.attachment_path}
                                                                                                        target="_blank"
                                                                                                        rel="noopener noreferrer"
                                                                                                        className="text-[#C30010] hover:underline truncate max-w-[60px] font-semibold text-[10px]"
                                                                                                    >
                                                                                                        {editForm.attachment_names || 'File'}
                                                                                                    </a>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={() => handleRemoveAttachment(rev.id)}
                                                                                                        className="text-red-500 hover:text-red-700"
                                                                                                    >
                                                                                                        <FiX size={12} />
                                                                                                    </button>
                                                                                                </div>
                                                                                            ) : (
                                                                                                <label className="cursor-pointer inline-flex items-center gap-1 px-2 py-1 border border-dashed border-gray-300 hover:border-[#C30010] bg-gray-50 hover:bg-red-50 rounded transition-all group text-[9px] font-medium text-gray-500 group-hover:text-[#C30010] animate-fade-in" title="Click to upload">
                                                                                                    <FiUploadCloud size={12} className="text-gray-400 group-hover:text-[#C30010] shrink-0 transition-colors" />
                                                                                                    <span>Upload</span>
                                                                                                    <input type="file" className="hidden" onChange={(e) => handleFileChange(rev.id, e.target.files[0])} accept="image/*,application/pdf" />
                                                                                                </label>
                                                                                            )}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="px-4 py-3 w-[10%] align-middle text-right">
                                                                                        <div className="flex flex-col items-end gap-1">
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => handleEditSubmit(rev.id)}
                                                                                                disabled={saving}
                                                                                                className="w-14 py-1 text-[11px] font-bold text-white bg-[#C30010] hover:bg-[#a0000d] rounded shadow transition disabled:opacity-60 text-center"
                                                                                            >
                                                                                                Save
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => setEditingRowId(null)}
                                                                                                className="w-14 py-1 text-[11px] font-bold text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-55 transition text-center"
                                                                                            >
                                                                                                Cancel
                                                                                            </button>
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        }

                                                                        return (
                                                                            <tr key={rev.id} className="align-middle hover:bg-gray-55/50 transition border-b border-gray-100">
                                                                                <td className="px-4 py-3 w-[14%] align-middle text-gray-500 font-medium">
                                                                                    {formatDateToDisplay(prevRevisionMonth)}
                                                                                </td>
                                                                                <td className="px-4 py-3 w-[14%] align-middle text-gray-708 font-semibold">
                                                                                    {formatDateToDisplay(rev.revision_mmyy)}
                                                                                </td>
                                                                                <td className="px-4 py-3 w-[27%] align-middle">
                                                                                    <div className="text-gray-600 text-xs whitespace-pre-wrap break-words w-full leading-relaxed">
                                                                                        {rev.performance_revision_reason || '—'}
                                                                                    </div>
                                                                                </td>
                                                                                <td className="px-4 py-3 w-[26%] align-middle">
                                                                                    {isEditingReply ? (
                                                                                        <textarea
                                                                                            rows={2}
                                                                                            value={employeeReplyText}
                                                                                            onChange={(e) => setEmployeeReplyText(e.target.value)}
                                                                                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#C30010] resize-y min-h-[42px] leading-normal bg-white"
                                                                                            placeholder="Enter your reply"
                                                                                        />
                                                                                    ) : (
                                                                                        <div className="text-gray-705 text-xs whitespace-pre-wrap break-words w-full leading-relaxed">
                                                                                            {rev.performance_revision_employee_reply || '—'}
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                                <td className="px-4 py-3 w-[9%] align-middle">
                                                                                    {rev.attachment_path ? (
                                                                                        <a
                                                                                            href={rev.attachment_path}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className="inline-flex items-center gap-1 text-[#C30010] hover:underline font-semibold text-[10px]"
                                                                                            title={rev.attachment_names || 'View attachment'}
                                                                                        >
                                                                                            <FiPaperclip size={12} className="shrink-0" />
                                                                                            <span className="truncate max-w-[60px]">{rev.attachment_names || 'File'}</span>
                                                                                        </a>
                                                                                    ) : (
                                                                                        <span className="text-gray-400 text-xs">—</span>
                                                                                    )}
                                                                                </td>
                                                                                <td className="px-4 py-3 w-[10%] align-middle text-right">
                                                                                    {isAdmin ? (
                                                                                        <div className="flex items-center justify-end gap-1.5">
                                                                                            {rev.id !== 'default' && (
                                                                                                <>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={() => handleEditClick(rev)}
                                                                                                        className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-[#C30010] transition"
                                                                                                        title="Edit revision"
                                                                                                    >
                                                                                                        <FiEdit size={14} />
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={() => {
                                                                                                            if (window.confirm('Are you sure you want to delete this performance revision?')) {
                                                                                                                onDelete(rev.id);
                                                                                                            }
                                                                                                        }}
                                                                                                        className="p-1 hover:bg-red-50 rounded text-gray-500 hover:text-[#C30010] transition"
                                                                                                        title="Delete revision"
                                                                                                    >
                                                                                                        <FiTrash2 size={14} />
                                                                                                    </button>
                                                                                                </>
                                                                                            )}
                                                                                        </div>
                                                                                    ) : (
                                                                                        <div className="flex items-center justify-end gap-1">
                                                                                            {!isEditingReply && rev.id !== 'default' && (
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => {
                                                                                                        setEditingReplyId(rev.id);
                                                                                                        setEmployeeReplyText(rev.performance_revision_employee_reply || '');
                                                                                                    }}
                                                                                                    className="px-2 py-0.5 text-[10px] font-bold text-[#C30010] bg-red-50 hover:bg-red-100 rounded transition shrink-0"
                                                                                                >
                                                                                                    Reply
                                                                                                </button>
                                                                                            )}
                                                                                            {isEditingReply && (
                                                                                                <div className="flex flex-col items-end gap-1 shrink-0">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={() => handleReplySubmit(rev.id)}
                                                                                                        disabled={saving}
                                                                                                        className="w-12 py-0.5 text-[9px] font-bold text-white bg-[#C30010] hover:bg-[#a0000d] rounded shadow transition disabled:opacity-60 text-center"
                                                                                                    >
                                                                                                        Save
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={() => setEditingReplyId(null)}
                                                                                                        className="w-12 py-0.5 text-[9px] font-bold text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-55 transition text-center"
                                                                                                    >
                                                                                                        Cancel
                                                                                                    </button>
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    });
                                                                })()}
                                                            </tbody>
                        </table>
                    </div>
                </div>
                {renderDetailsModal()}
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-55/80">
                    <div className="flex items-center gap-2 min-w-0">
                        <div className="p-2 bg-[#ffebee] text-[#C30010] rounded-lg shrink-0">
                            <FiAward size={18} />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Performance Revision</h3>
                            <p className="text-[10px] text-gray-400 mt-0.5">
                                {isAdmin ? 'Create or update performance revision details' : 'Review details and submit your reply'}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition"
                        aria-label="Close"
                    >
                        <FiX size={18} />
                    </button>
                </div>

                <div className="p-5 sm:p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FieldShell label="Employee ID">
                            {canEdit('employee_id') ? (
                                <input
                                    type="text"
                                    value={form.employee_id}
                                    onChange={(e) => setField('employee_id', e.target.value)}
                                    className={inputClass('employee_id')}
                                />
                            ) : (
                                <ReadOnlyValue value={form.employee_id} />
                            )}
                        </FieldShell>

                        <FieldShell label="Employee Name">
                            {canEdit('employee_name') ? (
                                <input
                                    type="text"
                                    value={form.employee_name}
                                    onChange={(e) => setField('employee_name', e.target.value)}
                                    className={inputClass('employee_name')}
                                />
                            ) : (
                                <ReadOnlyValue value={form.employee_name} />
                            )}
                        </FieldShell>

                        <FieldShell label="Role">
                            {canEdit('role') ? (
                                <input
                                    type="text"
                                    value={form.role}
                                    onChange={(e) => setField('role', e.target.value)}
                                    className={inputClass('role')}
                                />
                            ) : (
                                <ReadOnlyValue value={form.role} />
                            )}
                        </FieldShell>

                        <FieldShell label="Performance Revision Date (DD/MM/YYYY)" error={errors.revision_month}>
                            {canEdit('revision_month') ? (
                                <input
                                    type="date"
                                    value={dateToInputFormat(form.revision_month)}
                                    onChange={(e) => setField('revision_month', dateToDisplayFormat(e.target.value))}
                                    className={`w-full px-3 py-2.5 text-sm font-medium border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#C30010]/20 ${errors.revision_month ? 'border-red-400' : 'border-gray-200'}`}
                                />
                            ) : (
                                <ReadOnlyValue value={form.revision_month} />
                            )}
                        </FieldShell>

                        <div className="md:col-span-2">
                            <FieldShell label="Performance Feedback">
                                {canEdit('reason') ? (
                                    <textarea
                                        rows={3}
                                        value={form.reason}
                                        onChange={(e) => setField('reason', e.target.value)}
                                        className={`${inputClass('reason')} resize-y min-h-[88px]`}
                                        placeholder="Enter performance feedback for this revision"
                                    />
                                ) : (
                                    <div className="w-full px-3 py-2.5 text-sm text-gray-855 bg-gray-55 border border-gray-200 rounded-lg min-h-[88px] whitespace-pre-wrap">
                                        {form.reason || '—'}
                                    </div>
                                )}
                            </FieldShell>
                        </div>

                        <div className="md:col-span-2">
                            <FieldShell label="Employee Reply">
                                {canEdit('employee_reply') ? (
                                    <textarea
                                        rows={3}
                                        value={form.employee_reply}
                                        onChange={(e) => setField('employee_reply', e.target.value)}
                                        className={`${inputClass('employee_reply')} resize-y min-h-[88px]`}
                                        placeholder="Enter employee reply"
                                    />
                                ) : (
                                    <div className="w-full px-3 py-2.5 text-sm text-gray-855 bg-gray-55 border border-gray-200 rounded-lg min-h-[88px] whitespace-pre-wrap">
                                        {form.employee_reply || '—'}
                                    </div>
                                )}
                            </FieldShell>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100 bg-gray-55/80">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-bold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-55 transition"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={saving}
                        className="px-4 py-2 text-sm font-bold text-white bg-[#C30010] hover:bg-[#a0000d] rounded-lg shadow-md shadow-red-100 transition flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        <FiSave size={16} />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
                {renderDetailsModal()}
            </div>
        </div>
    );
};

export default EmployeePerformanceRevisionModal;
